import React, { useState, useEffect } from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';
import { parseHeight, heightLabel } from '../../utils/height';

// ── Rentals and demos settings (4 Oct) ─────────────────────────────────────────
//
// Under Online Booking, saved with the page's Save button as
// settings.booking.rentals. The rules that use them are in
// server/helpers/rentals.js.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const newId = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

// A height box that shows 5′7″ and reads whatever is typed when it loses focus.
function HeightInput({ value, onChange, style, placeholder }) {
  const [text, setText] = useState(null);
  return (
    <input value={text != null ? text : heightLabel(value)} placeholder={placeholder}
      onFocus={() => setText(heightLabel(value))}
      onChange={e => setText(e.target.value)}
      onBlur={() => { const v = parseHeight(text); onChange(text && text.trim() && v == null ? value : v); setText(null); }}
      style={style} />
  );
}

// Settings saved before models (one row per bike, each with its own name,
// item and price) become one model per bike the first time they are edited.
function asModels(r) {
  const fleet = Array.isArray(r.fleet) ? r.fleet : [];
  const models = Array.isArray(r.models) ? [...r.models] : [];
  const out = fleet.map(x => {
    if (x.modelId) return x;
    const id = `m-${x.id}`;
    if (!models.some(m => m.id === id)) models.push({ id, name: x.name || '', itemID: x.itemID || null, itemName: x.itemName || '', rate: x.rate ?? 0, hold: x.hold ?? 0, demo: !!x.demo, category: '' });
    return { id: x.id, modelId: id, size: x.size || '', online: x.online !== false, inService: !!x.inService };
  });
  return { models, fleet: out };
}
// The model's photo (8 Oct): one picture for all its sizes, shown on the
// booking page. Spinboard shrinks it on upload, so a phone photo is fine.
// Goes live with the page's Save, like any other fleet change.
const photoSrc = (key) => (key && key.startsWith('rental-photos/') ? `${API}/book/rent/photo/${key.slice('rental-photos/'.length)}` : null);
function ModelPhoto({ m, onChange, T }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = React.useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API}/book/rent/staff/photo`, { method: 'POST', credentials: 'include', headers: authHeaders(), body: form });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.photoKey) throw new Error(d.error || 'The photo could not be uploaded.');
      onChange(d.photoKey);
    } catch (e) { setErr(e.message); }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };
  const src = photoSrc(m.photoKey);
  const btn = { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 10px', fontSize: 12, color: T.textSub, cursor: 'pointer', fontFamily: 'inherit' };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      <div style={{ width: 96, height: 64, borderRadius: 6, border: `1px ${src ? 'solid' : 'dashed'} ${T.border}`, background: '#fff', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {src ? <img src={src} alt={m.name || 'Bike'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <span style={{ fontSize: 11, color: '#8a94a6' }}>No photo</span>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => pick(e.target.files && e.target.files[0])} />
      <button type="button" disabled={busy} onClick={() => inputRef.current && inputRef.current.click()} style={btn}>
        {busy ? 'Uploading…' : src ? 'Change photo' : 'Add photo'}
      </button>
      {src && !busy && (
        <button type="button" onClick={() => onChange(null)} style={{ ...btn, border: 'none', textDecoration: 'underline' }}>Remove</button>
      )}
      <span style={{ fontSize: 12, color: err ? '#e74c3c' : T.textSub }}>
        {err || 'Shown on the booking page for every size of this model. A phone photo is fine; Spinboard shrinks it.'}
      </span>
    </div>
  );
}

const DEFAULT_QUESTIONS = [
  { id: 'height', label: 'Height' },
  { id: 'weight', label: 'Weight' },
  { id: 'pedals', label: 'Pedals (flat, clipless, bringing my own)' },
];

export default function SettingsRentals() {
  const { T, isDark, settings, setBooking, allStatuses = [], bookingBase = '' } = useSettings();
  const [copied, setCopied] = useState(null);
  const rentalsLink = `${bookingBase}/rentals`;
  const copy = (key, text) => { try { navigator.clipboard.writeText(text); } catch (_) {} setCopied(key); setTimeout(() => setCopied(c => (c === key ? null : c)), 2000); };
  const copyBtn = (key, text, label, title) => (
    <button type="button" title={title} onClick={() => copy(key, text)}
      style={{ background: 'transparent', border: `1px solid ${copied === key ? '#2ecc71' : T.border}`, borderRadius: 6, padding: '3px 10px', fontSize: 11, color: copied === key ? '#2ecc71' : T.textSub, cursor: 'pointer', fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap' }}>
      {copied === key ? '✓ Copied' : label}
    </button>
  );
  const b = settings.booking || {};
  const r = b.rentals || {};
  const { models, fleet } = asModels(r);
  const categories = Array.isArray(r.categories) ? r.categories : [];
  const questions = Array.isArray(r.questions) ? r.questions : DEFAULT_QUESTIONS;
  const openDays = Array.isArray(r.openDays) ? r.openDays : (b.openDays || [2, 3, 4, 5, 6]);
  const setRent = (patch) => setBooking({ rentals: { ...r, ...patch } });
  const setModel = (id, patch) => setRent({ models: models.map(m => m.id === id ? { ...m, ...patch } : m), fleet });
  const setBike = (id, patch) => setRent({ models, fleet: fleet.map(x => x.id === id ? { ...x, ...patch } : x) });
  // Lightspeed's price for each chosen item now. The rate saved on a model is
  // only what the item cost when it was picked, and a booking charges the live
  // price, so the live one is what Settings shows (5 Oct: $125 shown, $160 charged).
  const [livePrices, setLivePrices] = useState({});
  // Saved items Lightspeed treats as labor: it will not put them on a rental ticket.
  const [laborItems, setLaborItems] = useState({});
  const itemIds = [...new Set(models.flatMap(m => [m.itemID, m.weekendItemID]).filter(Boolean).map(String))].sort().join(',');
  useEffect(() => {
    if (!itemIds) return undefined;
    let on = true;
    fetch(`${API}/book/rent/staff/prices?ids=${itemIds}`, { credentials: 'include', headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (on && d && d.prices) { setLivePrices(d.prices); setLaborItems(d.labor || {}); } })
      .catch(() => {});
    return () => { on = false; };
  }, [itemIds]);
  const priceOf = (id, saved) => (id && livePrices[String(id)] != null ? livePrices[String(id)] : saved);
  const [picking, setPicking] = useState(null);      // { modelId, which: 'daily' | 'weekend' } whose item is being chosen
  const [newCat, setNewCat] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [hiddenLabor, setHiddenLabor] = useState(0);
  const [searching, setSearching] = useState(false);

  const search = async (text) => {
    setQ(text);
    if (text.trim().length < 2) { setFound([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/book/rent/staff/items?q=${encodeURIComponent(text.trim())}`, { credentials: 'include', headers: authHeaders() });
      const d = await res.json();
      setFound(Array.isArray(d.items) ? d.items : []);
      setHiddenLabor(Number(d.hiddenLabor) || 0);
    } catch (_) { setFound([]); setHiddenLabor(0); }
    setSearching(false);
  };

  const input = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '6px 9px', color: T.text, fontSize: 13, fontFamily: 'Arial, sans-serif', outline: 'none', boxSizing: 'border-box' };
  const sub = { fontSize: 12, color: T.textSub, lineHeight: 1.5 };
  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  const num = (key, fallback, min, max, width = 64) => (
    <input type="number" min={min} max={max} value={r[key] ?? fallback}
      onChange={e => setRent({ [key]: e.target.value === '' ? '' : Math.max(min, Math.min(max, Number(e.target.value) || 0)) })}
      style={{ ...input, width }} />
  );
  const section = (n, title, desc) => (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.cardBorder}` }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{n} · {title}</div>
      {desc ? <div style={{ ...sub, marginTop: 3, marginBottom: 10 }}>{desc}</div> : <div style={{ height: 8 }} />}
    </div>
  );
  const row = (label, children) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: T.text, minWidth: 230 }}>{label}</span>{children}
    </div>
  );
  // Done & Paid closes a ticket in Lightspeed for good: never a status to put a rental in.
  const statuses = allStatuses.filter(st => { const n = (st.name || '').toLowerCase(); return !(n.includes('done') && n.includes('paid')); });
  const statusPick = (key, emptyLabel) => (
    <select value={r[key] || ''} onChange={e => setRent({ [key]: e.target.value || null })} style={{ ...input, minWidth: 240 }}>
      <option value="">{emptyLabel}</option>
      {statuses.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
    </select>
  );
  const modelOf = (id) => models.find(m => m.id === id) || {};
  const live = fleet.filter(x => x.online !== false && !x.inService && modelOf(x.modelId).itemID).length;
  const weekend = r.weekend || {};
  const nextMorning = r.nextMorning || {};
  const isPicking = (modelId, which) => picking && picking.modelId === modelId && picking.which === which;
  const itemPicker = (m, which) => {
    const idKey = which === 'weekend' ? 'weekendItemID' : 'itemID';
    const nameKey = which === 'weekend' ? 'weekendItemName' : 'itemName';
    const rateKey = which === 'weekend' ? 'weekendRate' : 'rate';
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: T.textSub, minWidth: 150 }}>{which === 'weekend' ? 'Lightspeed weekend item:' : 'Lightspeed rental item:'}</span>
        {m[idKey]
          ? <span style={{ fontSize: 13, color: T.text }}>{m[nameKey] || `Item #${m[idKey]}`}{priceOf(m[idKey], m[rateKey]) != null && priceOf(m[idKey], m[rateKey]) !== '' ? ` · $${Number(priceOf(m[idKey], m[rateKey])).toFixed(2)} ${which === 'weekend' ? (weekend.mode === 'replace' ? 'for the weekend' : 'weekend fee, added on top') : 'per day'}` : ''}</span>
          : which === 'weekend'
            ? <span style={{ fontSize: 12, color: T.textSub }}>none: charged by the day</span>
            : <span style={{ fontSize: 12, color: '#e67e22' }}>not set: these bikes cannot be booked until it has one</span>}
        {m[idKey] && laborItems[String(m[idKey])] && (
          <span style={{ flexBasis: '100%', fontSize: 12, color: '#e74c3c', lineHeight: 1.5 }}>
            This is a labor item. Lightspeed will not put it on a rental ticket, so bookings of these bikes reach the ticket with no charge. Choose a product or other non-labor item instead.
          </span>
        )}
        <button type="button" onClick={() => { setPicking(isPicking(m.id, which) ? null : { modelId: m.id, which }); setQ(''); setFound([]); }}
          style={{ ...S.btn(false), fontSize: 12, padding: '3px 10px', color: T.textSub, borderColor: T.border }}>{isPicking(m.id, which) ? 'Close' : m[idKey] ? 'Change' : 'Choose'}</button>
        {which === 'weekend' && m[idKey] && (
          <button type="button" onClick={() => setModel(m.id, { weekendItemID: null, weekendItemName: '', weekendRate: null })}
            style={{ background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>Remove</button>
        )}
        {isPicking(m.id, which) && (
          <div style={{ flexBasis: '100%', marginTop: 4 }}>
            <input autoFocus value={q} placeholder={which === 'weekend' ? 'Search Lightspeed items, e.g. Weekend' : 'Search Lightspeed items, e.g. Rental'} onChange={e => search(e.target.value)} style={{ ...input, width: 320 }} />
            <div style={{ display: 'grid', gap: 4, marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
              {searching && <div style={sub}>Searching…</div>}
              {!searching && q.trim().length >= 2 && found.length === 0 && <div style={sub}>No items match.</div>}
              {!searching && q.trim().length >= 2 && hiddenLabor > 0 && (
                <div style={sub}>{hiddenLabor} labor item{hiddenLabor === 1 ? '' : 's'} hidden: Lightspeed cannot put a labor item on a rental ticket. Use an item with a non-labor tax class.</div>
              )}
              {found.map(it => (
                <button type="button" key={it.itemID}
                  onClick={() => { setModel(m.id, { [idKey]: it.itemID, [nameKey]: it.name, [rateKey]: it.price ?? m[rateKey] ?? 0 }); setPicking(null); }}
                  style={{ textAlign: 'left', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: T.text, fontSize: 13, fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{it.name}</span><span style={{ color: T.textSub }}>{it.price != null ? `$${it.price.toFixed(2)}` : ''}</span>
                </button>
              ))}
            </div>
            <div style={{ ...sub, marginTop: 4 }}>{which === 'weekend'
              ? (weekend.mode === 'replace' ? "The item's price is the whole weekend, per bike. It goes on the ticket once, instead of the daily item." : "The item's price is the weekend fee, per bike. It goes on the ticket once, beside the daily item.")
              : "The item's price is the daily rate. Change the price in Lightspeed and the booking page follows."}
              {' '}Use a product or other non-labor item: Lightspeed cannot put a labor item on a rental ticket, so labor items are not listed.</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Rentals and demos</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
        Specific bikes booked for a run of days, listed on the booking page by model and size.
        {live > 0
          ? <span style={{ color: '#2ecc71' }}> {live} bike{live === 1 ? '' : 's'} bookable online.</span>
          : <span> Off until a bike has a Lightspeed rental item and is set to show online.</span>}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12, background: tile, borderRadius: 8, padding: '10px 12px' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Rentals booking page</span>
        <span style={{ fontSize: 12.5, color: T.textSub, fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>{rentalsLink}</span>
        {copyBtn('page', rentalsLink, '🔗 Copy link', 'Its own page, separate from service booking. Use it on your website or text it to a customer.')}
        <span style={sub}>Each model below has its own link too, which opens on that bike.</span>
      </div>

      {/* Optional (8 Oct): the fleet as cards on the shop's own website, for a
          shop without its own rentals pages. The link above stays for shops
          that have them. */}
      {settings.shopSlug && (() => {
        const snippet = `<script src="${API}/book/rent/embed.js?shop=${settings.shopSlug}" async></script>`;
        return (
          <div style={{ marginTop: 8, background: tile, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Website block</span>
              <span style={sub}>Optional. Shows your fleet as cards, with photos and prices, right on your own website.</span>
              {copyBtn('embed', snippet, '📋 Copy code', 'Paste it into an embed or code block on your website.')}
              <a href={`${API}/book/rent/embed-preview?shop=${encodeURIComponent(settings.shopSlug)}`} target="_blank" rel="noopener noreferrer"
                title="Opens a sample page with the block on it. Save first: it shows what is saved."
                style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 10px', fontSize: 11, color: T.textSub, textDecoration: 'none', whiteSpace: 'nowrap', fontFamily: "'Inter',sans-serif" }}>
                👁 Preview
              </a>
            </div>
            <div style={{ fontSize: 12, color: T.textSub, fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all', marginTop: 6 }}>{snippet}</div>
            <div style={{ ...sub, marginTop: 6 }}>
              Paste it once into an embed or code block on the page where the cards should appear. Changes you save here reach your website within about five minutes.
              Each card's Book button opens this booking page on that bike.
            </div>
          </div>
        );
      })()}

      {section(1, 'Your fleet', 'A model holds the price, category, damage hold and Lightspeed items. Each size row under it is one real bike, with the rider heights it fits: two of the same size are two rows, so the calendar knows exactly which is out.')}
      <div style={{ display: 'grid', gap: 10 }}>
        {models.map(m => {
          const bikes = fleet.filter(x => x.modelId === m.id);
          return (
            <div key={m.id} style={{ background: tile, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={m.name || ''} placeholder="Model, e.g. Mondraker Raze Carbon R" onChange={e => setModel(m.id, { name: e.target.value })} style={{ ...input, flex: '2 1 220px', fontWeight: 600 }} />
                <select value={m.category || ''} onChange={e => setModel(m.id, { category: e.target.value })} style={{ ...input, width: 120 }}>
                  <option value="">No category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span style={{ fontSize: 12, color: T.textSub }}>Damage hold $</span>
                <input type="number" min="0" value={m.hold ?? ''} onChange={e => setModel(m.id, { hold: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) })} style={{ ...input, width: 80 }} />
                <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: T.text }}>
                  <input type="checkbox" checked={!!m.demo} onChange={e => setModel(m.id, { demo: e.target.checked })} style={{ accentColor: '#F4622A' }} /> Demo
                </label>
                {copyBtn(`m-${m.id}`, `${rentalsLink}?model=${encodeURIComponent(m.id)}`, '🔗 Copy link', 'The rentals page opened on this bike. Save first if you just added it.')}
                <button type="button" title="Remove this model and its bikes" onClick={() => setRent({ models: models.filter(x => x.id !== m.id), fleet: fleet.filter(x => x.modelId !== m.id) })}
                  style={{ background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 15 }}>×</button>
              </div>
              <ModelPhoto m={m} T={T} onChange={key => setModel(m.id, { photoKey: key })} />
              <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                {itemPicker(m, 'daily')}
                {weekend.on && itemPicker(m, 'weekend')}
              </div>
              <div style={{ marginTop: 10, borderTop: `1px solid ${T.cardBorder}`, paddingTop: 6 }}>
                {bikes.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '70px 210px 70px 120px 24px', gap: 8, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textSub, padding: '2px 0' }}>
                    <span>Size</span><span>Rider height</span><span>Online</span><span>In for service</span><span />
                  </div>
                )}
                {bikes.map(bike => (
                  <div key={bike.id} style={{ display: 'grid', gridTemplateColumns: '70px 210px 70px 120px 24px', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                    <input value={bike.size || ''} placeholder="Size" onChange={e => setBike(bike.id, { size: e.target.value })} style={{ ...input, width: 64 }} />
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <HeightInput value={bike.heightMin} placeholder="5′7″" onChange={v => setBike(bike.id, { heightMin: v })} style={{ ...input, width: 80 }} />
                      <span style={sub}>to</span>
                      <HeightInput value={bike.heightMax} placeholder="5′11″" onChange={v => setBike(bike.id, { heightMax: v })} style={{ ...input, width: 80 }} />
                    </span>
                    <input type="checkbox" title="Shown on the booking page" checked={bike.online !== false} onChange={e => setBike(bike.id, { online: e.target.checked })} style={{ accentColor: '#F4622A', justifySelf: 'start' }} />
                    <input type="checkbox" title="Not bookable at all, by anyone, until unticked" checked={!!bike.inService} onChange={e => setBike(bike.id, { inService: e.target.checked })} style={{ accentColor: '#F4622A', justifySelf: 'start' }} />
                    <button type="button" title="Remove this bike" onClick={() => setRent({ models, fleet: fleet.filter(x => x.id !== bike.id) })}
                      style={{ background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 15 }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={() => setRent({ models, fleet: [...fleet, { id: newId('b'), modelId: m.id, size: '', heightMin: null, heightMax: null, online: true }] })}
                  style={{ background: 'transparent', border: 'none', color: '#F4622A', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, padding: '6px 0', fontFamily: 'inherit' }}>+ Add a size</button>
                <span style={{ ...sub, marginLeft: 8 }}>Two of the same size? Add the size twice, with the same heights.</span>
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button"
            onClick={() => { const id = newId('m'); setRent({ models: [...models, { id, name: '', category: '', hold: 500, demo: false }], fleet: [...fleet, { id: newId('b'), modelId: id, size: '', online: true }] }); setPicking({ modelId: id, which: 'daily' }); setQ(''); setFound([]); }}
            style={{ ...S.btn(false), fontSize: 12, color: '#F4622A', borderColor: '#F4622A55' }}>+ Add a model</button>
          <span style={{ fontSize: 12, color: T.textSub }}>Categories:</span>
          {categories.map(c => (
            <span key={c} style={{ fontSize: 12, padding: '3px 4px 3px 9px', borderRadius: 12, border: `1px solid ${T.inputBorder}`, color: T.text, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{c}
              <button type="button" title={`Remove ${c}`} onClick={() => setRent({ models: models.map(m => m.category === c ? { ...m, category: '' } : m), fleet, categories: categories.filter(x => x !== c) })}
                style={{ background: 'transparent', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 13, padding: 0 }}>×</button></span>
          ))}
          <input value={newCat} placeholder="Add, e.g. eMTB" onChange={e => setNewCat(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newCat.trim() && !categories.includes(newCat.trim())) { setRent({ models, fleet, categories: [...categories, newCat.trim()] }); setNewCat(''); } }}
            style={{ ...input, width: 130, fontSize: 12 }} />
        </div>
      </div>

      {section(2, 'Booking rules')}
      {row('Pick up from', <><input type="time" step="900" value={r.pickupTime || '10:00'} onChange={e => setRent({ pickupTime: e.target.value })} style={input} /><span style={sub}>on the first day</span></>)}
      {row('Return by', <><input type="time" step="900" value={r.returnTime || '17:00'} onChange={e => setRent({ returnTime: e.target.value })} style={input} /><span style={sub}>on the last day</span></>)}
      {row('Rental length', <>{num('minDays', 1, 1, 60)}<span style={sub}>to</span>{num('maxDays', 14, 1, 60)}<span style={sub}>days. A day is a calendar day: Thursday to Saturday is 3.</span></>)}
      {row('Book at least', <>{num('noticeDays', 1, 0, 30)}<span style={sub}>days ahead, up to</span>{num('windowDays', 60, 7, 365)}<span style={sub}>days out</span></>)}
      {row('Pick-up and return days', (
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAYS.map((d, i) => {
            const on = openDays.includes(i);
            return (
              <button type="button" key={d} onClick={() => setRent({ openDays: on ? openDays.filter(x => x !== i) : [...openDays, i].sort() })}
                style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${on ? '#F4622A' : T.inputBorder}`, background: on ? '#F4622A22' : 'transparent', color: on ? '#F4622A' : T.textSub, fontWeight: on ? 700 : 400 }}>{d}</button>
            );
          })}
        </span>
      ))}
      <div style={{ ...sub, marginLeft: 240, marginTop: -2 }}>A rental can run over a closed day. Blackout dates above apply too.</div>
      {row('Free cancellation until', <>{num('freeCancelHours', 48, 0, 336)}<span style={sub}>hours before pick-up (later than that, they call)</span></>)}
      <div style={{ margin: '10px 0' }}>
        <div style={{ fontSize: 13, color: T.text, marginBottom: 6 }}>Note on the booking page <span style={sub}>(optional, shown under the bikes)</span></div>
        <textarea rows={2} value={r.disclaimerText || ''} placeholder="e.g. Please bring a photo ID and your own pedals and helmet."
          onChange={e => setRent({ disclaimerText: e.target.value })}
          style={{ ...input, width: '100%', resize: 'vertical', lineHeight: 1.5 }} />
      </div>
      {row("Ask for the customer's address", <><input type="checkbox" checked={!!r.askAddress} onChange={e => setRent({ askAddress: e.target.checked })} style={{ accentColor: '#F4622A' }} /><span style={sub}>optional on the booking page; it goes on the ticket</span></>)}

      {section(3, 'Pricing', 'How many days a rental is charged. The ticket gets each bike\'s Lightspeed item at that quantity, so the register charges what the booking page showed.')}
      <div style={{ display: 'flex', gap: 10, margin: '8px 0', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <span style={{ fontSize: 13, color: T.text, minWidth: 230, paddingTop: 2 }}>How rental days are charged</span>
        <span style={{ display: 'grid', gap: 6 }}>
          {[['calendar', 'Every calendar day', 'Saturday to Monday is 3 days'], ['open', 'Only days the shop is open', 'Saturday to Monday, closed Sunday, is 2 days']].map(([v, label, hint]) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: T.text, cursor: 'pointer' }}>
              <input type="radio" name="rentDayMode" checked={(r.dayMode || 'calendar') === v} onChange={() => setRent({ dayMode: v })} style={{ accentColor: '#F4622A', marginTop: 2 }} />
              <span>{label}<br /><span style={sub}>{hint}</span></span>
            </label>
          ))}
        </span>
      </div>
      {row('Weekend rate', (
        <>
          <input type="checkbox" checked={!!weekend.on} onChange={e => setRent({ weekend: { pickupDay: 6, returnDay: 1, ...weekend, on: e.target.checked } })} style={{ accentColor: '#F4622A' }} />
          <select disabled={!weekend.on} value={weekend.match === 'dueBack' ? 'dueBack' : 'exact'} onChange={e => setRent({ weekend: { dueBackDays: [0, 1], ...weekend, match: e.target.value } })} style={input}>
            <option value="exact">for a booking picked up</option>
            <option value="dueBack">for a booking due back on</option>
          </select>
          {weekend.match === 'dueBack' ? (
            <span style={{ display: 'inline-flex', gap: 4 }}>
              {DAY_NAMES.map((d, i) => {
                const days = Array.isArray(weekend.dueBackDays) ? weekend.dueBackDays : [0, 1];
                const onDay = days.includes(i);
                return (
                  <button type="button" key={d} disabled={!weekend.on} aria-pressed={onDay}
                    onClick={() => setRent({ weekend: { ...weekend, dueBackDays: onDay ? days.filter(x => x !== i) : [...days, i].sort() } })}
                    style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, cursor: weekend.on ? 'pointer' : 'default', fontFamily: 'inherit',
                      border: `1px solid ${onDay ? '#F4622A' : T.border}`, background: onDay ? '#F4622A22' : 'transparent', color: onDay ? '#F4622A' : T.textSub, fontWeight: onDay ? 700 : 400 }}>{d.slice(0, 3)}</button>
                );
              })}
            </span>
          ) : (
            <>
              <select disabled={!weekend.on} value={weekend.pickupDay ?? 6} onChange={e => setRent({ weekend: { ...weekend, pickupDay: Number(e.target.value) } })} style={input}>
                {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
              <span style={sub}>and returned</span>
              <select disabled={!weekend.on} value={weekend.returnDay ?? 1} onChange={e => setRent({ weekend: { ...weekend, returnDay: Number(e.target.value) } })} style={input}>
                {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </>
          )}
        </>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '-2px 0 8px 240px', flexWrap: 'wrap' }}>
        <span style={sub}>The weekend item</span>
        <select disabled={!weekend.on} value={weekend.mode === 'replace' ? 'replace' : 'addon'} onChange={e => setRent({ weekend: { ...weekend, mode: e.target.value } })} style={input}>
          <option value="addon">is added to the days charged</option>
          <option value="replace">replaces the daily price</option>
        </select>
      </div>
      <div style={{ ...sub, marginLeft: 240, marginTop: -2, maxWidth: 620 }}>{weekend.match === 'dueBack'
        ? 'A booking due back on one of those days, that covers a Saturday or Sunday,'
        : 'A booking for exactly those days'}{weekend.mode === 'replace'
        ? " puts each model's weekend item on the ticket once, instead of the daily item."
        : " adds each model's weekend item to the ticket once, as a fee on top of the days charged."} Choose the weekend items in the fleet above; a model without one is charged by the day.</div>
      {weekend.on && openDays.includes(0) && (
        <div role="note" style={{ fontSize: 12.5, color: '#c0661a', marginLeft: 240, marginTop: 4, maxWidth: 620 }}>
          Sunday is a pick-up and return day (above). A weekend fee is meant for shops closed on Sunday, where a bike sits out over a day nobody can bring it back. With Sunday open, most shops leave the weekend rate off.
        </div>
      )}
      {row('Next-morning return', (
        <>
          <input type="checkbox" checked={!!nextMorning.on} onChange={e => setRent({ nextMorning: { by: '10:00', ...nextMorning, on: e.target.checked } })} style={{ accentColor: '#F4622A' }} />
          <span style={sub}>Back by</span>
          <input type="time" step="900" disabled={!nextMorning.on} value={nextMorning.by || '10:00'} onChange={e => setRent({ nextMorning: { ...nextMorning, by: e.target.value } })} style={input} />
          <span style={sub}>on the last day, and that day is not charged</span>
        </>
      ))}

      {section(4, 'Money and statuses')}
      <div style={{ display: 'flex', gap: 10, margin: '8px 0', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <span style={{ fontSize: 13, color: T.text, minWidth: 230, paddingTop: 2 }}>Taken online to hold the bikes</span>
        <span style={{ display: 'grid', gap: 6 }}>
          {[['firstDay', "The first day's rental"], ['flat', 'A flat amount per bike'], ['full', 'The whole rental']].map(([v, label]) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: T.text, cursor: 'pointer' }}>
              <input type="radio" name="rentDeposit" checked={(r.depositMode || 'firstDay') === v} onChange={() => setRent({ depositMode: v })} style={{ accentColor: '#F4622A' }} />
              {label}{v === 'flat' && (r.depositMode === 'flat') && <> $ {num('depositFlat', 50, 0, 10000, 80)}</>}
            </label>
          ))}
          <span style={sub}>{(b.paymentMode || 'off') === 'off' ? 'Payment at booking is off above, so nothing is taken online.' : 'Taken the same way as service bookings. Refunded if the customer cancels in time.'}</span>
        </span>
      </div>
      {row('Damage hold at pick-up', <span style={{ ...sub, maxWidth: 620 }}>Each bike's amount, above, is held on the customer's card when the bikes go out and released when they come back clean. Rentals over {num('chargeOverDays', 5, 1, 6, 56)} days are charged instead and refunded on return, since a hold can lapse after about a week.</span>)}
      {row('Demo fee', <span style={{ ...sub, maxWidth: 620 }}>Counts toward a new bike bought within {num('demoCreditDays', 30, 1, 365, 64)} days of the demo. After that the shop keeps it. Rentals never count toward a purchase.</span>)}
      {row('Lightspeed status for new rentals', statusPick('statusBookedId', 'Same as service bookings'))}
      {row('When the bikes go out', statusPick('statusOutId', 'Leave the status alone'))}
      {row('When they come back', statusPick('statusReturnedId', 'Leave the status alone'))}
      {row('Status on cancellation', statusPick('cancelStatusId', 'Same as service bookings'))}

      {section(5, 'Rider questions', 'Asked once per bike on the booking page. The answers go on the Lightspeed ticket.')}
      <div style={{ display: 'grid', gap: 6 }}>
        {questions.map((qq, i) => (
          <div key={qq.id} style={{ display: 'flex', gap: 6 }}>
            <input value={qq.label} placeholder="Question" onChange={e => setRent({ questions: questions.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} style={{ ...input, flex: 1 }} />
            <button type="button" title="Remove" onClick={() => setRent({ questions: questions.filter((_, j) => j !== i) })}
              style={{ background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 15 }}>×</button>
          </div>
        ))}
        <button type="button" onClick={() => setRent({ questions: [...questions, { id: `q${Date.now().toString(36)}`, label: '' }] })}
          style={{ ...S.btn(false), fontSize: 12, color: T.textSub, borderColor: T.border, justifySelf: 'start' }}>+ Add a question</button>
      </div>
    </div>
  );
}
